/**
 * Centralized localStorage key management for OPFS site tracking.
 *
 * OPFS data lives on the Playground origin and acts as a local cache.
 * localStorage (on the dashboard origin) tracks which sites have been
 * initialized in OPFS and when they were last accessed.
 *
 * All operations are wrapped in try-catch because localStorage is a caching
 * layer — failures should degrade gracefully, not crash the app.
 */

const PREFIX_INITIALIZED = 'miles-opfs-initialized-';
const PREFIX_LAST_ACCESSED = 'miles-opfs-last-accessed-';
const PREFIX_PLUGIN_VERSION = 'miles-opfs-plugin-version-';
const PREFIX_WP_VERSION = 'miles-opfs-wp-version-';
const PREFIX_SNAPSHOT_VERSION = 'miles-opfs-snapshot-version-';

export interface OpfsStoredVersions {
  pluginVersion: string | null;
  wpVersion: string | null;
  snapshotVersion?: number | null;
}

/** Check if a site has OPFS data cached */
export function isSiteInOpfs(siteId: string): boolean {
  try {
    return localStorage.getItem(`${PREFIX_INITIALIZED}${siteId}`) === 'true';
  } catch {
    return false;
  }
}

/** Mark a site as having OPFS data */
export function markSiteInOpfs(siteId: string): void {
  try {
    localStorage.setItem(`${PREFIX_INITIALIZED}${siteId}`, 'true');
  } catch (err) {
    console.warn('[opfs-storage-keys] Failed to mark site in OPFS:', err);
  }
}

/** Record current timestamp as last-accessed for LRU tracking */
export function touchSiteAccess(siteId: string): void {
  try {
    localStorage.setItem(`${PREFIX_LAST_ACCESSED}${siteId}`, String(Date.now()));
  } catch (err) {
    console.warn('[opfs-storage-keys] Failed to touch site access:', err);
  }
}

/** Get last-accessed timestamp for a site (0 if never recorded) */
export function getSiteLastAccessed(siteId: string): number {
  try {
    const val = localStorage.getItem(`${PREFIX_LAST_ACCESSED}${siteId}`);
    return val ? parseInt(val, 10) : 0;
  } catch {
    return 0;
  }
}

/** Get stored plugin, WP, and snapshot versions for a site */
export function getStoredVersions(siteId: string): {
  pluginVersion: string | null;
  wpVersion: string | null;
  snapshotVersion: number | null;
} {
  try {
    const snapshotStr = localStorage.getItem(`${PREFIX_SNAPSHOT_VERSION}${siteId}`);
    return {
      pluginVersion: localStorage.getItem(`${PREFIX_PLUGIN_VERSION}${siteId}`),
      wpVersion: localStorage.getItem(`${PREFIX_WP_VERSION}${siteId}`),
      snapshotVersion: snapshotStr !== null ? (parseInt(snapshotStr, 10) || 0) : null,
    };
  } catch (err) {
    console.warn('[opfs-storage-keys] Failed to read stored versions:', err);
    return { pluginVersion: null, wpVersion: null, snapshotVersion: null };
  }
}

/** Store plugin, WP, and snapshot versions for a site */
export function storeVersions(
  siteId: string,
  versions: OpfsStoredVersions,
): void {
  try {
    if (versions.pluginVersion) {
      localStorage.setItem(`${PREFIX_PLUGIN_VERSION}${siteId}`, versions.pluginVersion);
    } else {
      localStorage.removeItem(`${PREFIX_PLUGIN_VERSION}${siteId}`);
    }
    if (versions.wpVersion) {
      localStorage.setItem(`${PREFIX_WP_VERSION}${siteId}`, versions.wpVersion);
    } else {
      localStorage.removeItem(`${PREFIX_WP_VERSION}${siteId}`);
    }
    if (versions.snapshotVersion != null) {
      localStorage.setItem(`${PREFIX_SNAPSHOT_VERSION}${siteId}`, String(versions.snapshotVersion));
    } else {
      localStorage.removeItem(`${PREFIX_SNAPSHOT_VERSION}${siteId}`);
    }
  } catch (err) {
    console.warn('[opfs-storage-keys] Failed to store versions:', err);
  }
}

function writeVersionValue(key: string, value: string | null): void {
  if (value === null) {
    localStorage.removeItem(key);
    return;
  }

  localStorage.setItem(key, value);
}

/**
 * Strictly persist the baseline for a site recovered without a dashboard
 * marker. Versions are written and verified before the marker is committed.
 * If any write fails, the marker remains absent so the next boot repeats the
 * safe recovery path instead of trusting a partial baseline.
 */
export function persistRecoveredSiteState(
  siteId: string,
  versions: OpfsStoredVersions,
): void {
  const initializedKey = `${PREFIX_INITIALIZED}${siteId}`;
  const expectedValues = new Map<string, string | null>([
    [`${PREFIX_PLUGIN_VERSION}${siteId}`, versions.pluginVersion],
    [`${PREFIX_WP_VERSION}${siteId}`, versions.wpVersion],
    [
      `${PREFIX_SNAPSHOT_VERSION}${siteId}`,
      versions.snapshotVersion == null
        ? null
        : String(versions.snapshotVersion),
    ],
  ]);

  try {
    localStorage.removeItem(initializedKey);

    for (const [key, value] of expectedValues) {
      writeVersionValue(key, value);
    }

    for (const [key, expectedValue] of expectedValues) {
      if (localStorage.getItem(key) !== expectedValue) {
        throw new Error(`Failed to verify localStorage key ${key}`);
      }
    }

    localStorage.setItem(initializedKey, 'true');
    if (localStorage.getItem(initializedKey) !== 'true') {
      throw new Error(`Failed to verify localStorage key ${initializedKey}`);
    }
  } catch (error) {
    try {
      localStorage.removeItem(initializedKey);
    } catch (cleanupError) {
      console.warn(
        '[opfs-storage-keys] Failed to remove incomplete recovery marker:',
        cleanupError,
      );
    }

    throw error;
  }
}

/** Remove all localStorage markers for a site */
export function clearSiteMarkers(siteId: string): void {
  try {
    localStorage.removeItem(`${PREFIX_INITIALIZED}${siteId}`);
    localStorage.removeItem(`${PREFIX_LAST_ACCESSED}${siteId}`);
    localStorage.removeItem(`${PREFIX_PLUGIN_VERSION}${siteId}`);
    localStorage.removeItem(`${PREFIX_WP_VERSION}${siteId}`);
    localStorage.removeItem(`${PREFIX_SNAPSHOT_VERSION}${siteId}`);
  } catch (err) {
    console.warn('[opfs-storage-keys] Failed to clear site markers:', err);
  }
}

/**
 * Sort site IDs by last-accessed time, oldest first (best eviction candidates).
 */
export function sortSitesByLru(siteIds: string[]): string[] {
  return [...siteIds].sort(
    (a, b) => getSiteLastAccessed(a) - getSiteLastAccessed(b),
  );
}
